import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  NgZone,
  OnDestroy,
  ViewEncapsulation,
  afterNextRender,
  inject,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { WidgetRecaptchaService } from '../../services/recaptcha/widget-recaptcha.service';

const TOKEN_POLL_INTERVAL_MS = 400;
const TOKEN_POLL_MAX_MS = 5 * 60 * 1000;

@Component({
  standalone: true,
  selector: 'ufp-widget-recaptcha-v2-challenge',
  encapsulation: ViewEncapsulation.None,
  host: {
    class: 'ufp-widget-recaptcha-v2-challenge-host',
    '[class.ufp-widget-recaptcha-v2-challenge-host--body-mount]': 'usesBodyMount()',
  },
  template: `
    @if (isLoading()) {
      <p class="widget-recaptcha-v2-loading m-0" role="status">
        Loading security verification…
      </p>
    }
    <div
      #challengeContainer
      class="widget-recaptcha-v2-challenge-container"
      aria-label="security-verification-challenge"
      role="region"
      [class.widget-recaptcha-v2-challenge-container--hidden]="isLoading()"
    ></div>
    @if (loadError()) {
      <p class="widget-recaptcha-v2-error m-0" role="alert">
        Unable to load security verification. Please refresh and try again.
      </p>
    }
  `,
  styles: [
    `
      .ufp-widget-recaptcha-v2-challenge-host {
        display: block;
        position: relative;
        z-index: 10;
        overflow: visible;
        box-sizing: border-box;
        width: 100%;
        max-width: 100%;
        min-height: 74px;
        margin: 0.5rem 0 1rem;
      }
      .widget-recaptcha-v2-challenge-container {
        display: flex;
        justify-content: center;
        align-items: flex-start;
        padding: 0.25rem 0;
        min-height: 74px;
        width: 100%;
        max-width: 100%;
        overflow-x: auto;
        overflow-y: visible;
        -webkit-overflow-scrolling: touch;
      }
      .widget-recaptcha-v2-challenge-container--hidden {
        visibility: hidden;
        position: absolute;
        width: 1px;
        height: 1px;
        overflow: hidden;
        clip: rect(0, 0, 0, 0);
      }
      .widget-recaptcha-v2-loading {
        text-align: center;
        color: #475569;
        font-size: 0.875rem;
        padding: 0.5rem 0;
      }
      .widget-recaptcha-v2-error {
        text-align: center;
        color: #dc2626;
        font-size: 0.875rem;
        padding: 0.5rem 0;
      }
      .ufp-widget-recaptcha-v2-challenge-host--body-mount {
        min-height: 0;
        margin: 0;
      }
      .ufp-widget-recaptcha-v2-challenge-host--body-mount
        .widget-recaptcha-v2-challenge-container {
        display: none;
      }
    `,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WidgetRecaptchaV2ChallengeComponent implements OnDestroy {
  private readonly recaptchaService = inject(WidgetRecaptchaService);
  private readonly ngZone = inject(NgZone);
  private readonly hostRef = inject(ElementRef<HTMLElement>);
  private readonly cdr = inject(ChangeDetectorRef);

  readonly resolved = output<string>();
  readonly failed = output<void>();

  readonly loadError = signal(false);
  readonly isLoading = signal(true);
  readonly usesBodyMount = signal(false);

  private readonly challengeContainer =
    viewChild<ElementRef<HTMLElement>>('challengeContainer');

  private widgetId: number | undefined;
  private bodyOverlayRoot: HTMLElement | null = null;
  private tokenEmitted = false;
  private pollTimer: ReturnType<typeof setInterval> | null = null;
  private pollStartedAt = 0;

  // Runs reCAPTCHA render once the host view exists.
  private readonly _renderOnViewReady = afterNextRender(() => {
    void this.renderChallenge();
  });

  ngOnDestroy(): void {
    this.stopTokenPoll();
    this.removeBodyOverlay();
  }

  private async renderChallenge(): Promise<void> {
    this.isLoading.set(true);
    this.loadError.set(false);

    const container = this.resolveRenderContainer();
    if (!container) {
      this.failRender();
      return;
    }

    try {
      this.widgetId = await this.recaptchaService.renderV2(
        container,
        (token) => this.emitResolved(token),
        () => {
          this.ngZone.run(() => this.failed.emit());
        },
        () => {
          this.ngZone.run(() => this.failRender());
        },
      );
      this.isLoading.set(false);
      this.startTokenPoll();
      this.cdr.markForCheck();
    } catch {
      this.failRender();
    }
  }

  private failRender(): void {
    this.isLoading.set(false);
    this.loadError.set(true);
    this.removeBodyOverlay();
    this.usesBodyMount.set(false);
    this.cdr.markForCheck();
    this.failed.emit();
  }

  /** Inline in the form when possible; body modal only inside Shadow DOM widgets. */
  private resolveRenderContainer(): HTMLElement | null {
    const root = this.hostRef.nativeElement.getRootNode();
    if (root instanceof ShadowRoot) {
      this.usesBodyMount.set(true);
      this.cdr.markForCheck();
      return this.createBodyOverlayContainer();
    }

    return this.challengeContainer()?.nativeElement ?? null;
  }

  private createBodyOverlayContainer(): HTMLElement {
    document
      .querySelectorAll('[data-ufp-recaptcha-v2-overlay]')
      .forEach((node) => node.remove());

    const overlay = document.createElement('div');
    overlay.setAttribute('data-ufp-recaptcha-v2-overlay', 'true');
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-modal', 'true');
    overlay.setAttribute('aria-label', 'security-verification-challenge');
    Object.assign(overlay.style, {
      position: 'fixed',
      inset: '0',
      zIndex: '2147483647',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: 'rgba(15, 23, 42, 0.45)',
    });

    const panel = document.createElement('div');
    Object.assign(panel.style, {
      backgroundColor: '#fff',
      borderRadius: '8px',
      padding: '1rem 1.25rem',
      boxShadow: '0 10px 40px rgba(0, 0, 0, 0.2)',
      boxSizing: 'border-box',
      width: '100%',
      maxWidth: 'min(304px, calc(100vw - 2rem))',
      minHeight: '78px',
    });

    const challenge = document.createElement('div');
    challenge.className = 'widget-recaptcha-v2-challenge-container';
    panel.appendChild(challenge);
    overlay.appendChild(panel);
    document.body.appendChild(overlay);

    this.bodyOverlayRoot = overlay;
    return challenge;
  }

  private removeBodyOverlay(): void {
    this.bodyOverlayRoot?.remove();
    this.bodyOverlayRoot = null;
  }

  private emitResolved(token: string): void {
    if (this.tokenEmitted || !token?.trim()) {
      return;
    }
    this.tokenEmitted = true;
    this.stopTokenPoll();
    this.removeBodyOverlay();
    this.usesBodyMount.set(false);
    this.isLoading.set(false);
    this.cdr.markForCheck();
    this.ngZone.run(() => this.resolved.emit(token));
  }

  private startTokenPoll(): void {
    if (this.widgetId === undefined) {
      return;
    }

    this.pollStartedAt = Date.now();
    this.stopTokenPoll();
    this.pollTimer = setInterval(() => {
      if (this.tokenEmitted) {
        this.stopTokenPoll();
        return;
      }
      if (Date.now() - this.pollStartedAt > TOKEN_POLL_MAX_MS) {
        this.stopTokenPoll();
        return;
      }

      const token = this.recaptchaService.getV2Response(this.widgetId!);
      if (token) {
        this.emitResolved(token);
      }
    }, TOKEN_POLL_INTERVAL_MS);
  }

  private stopTokenPoll(): void {
    if (this.pollTimer !== null) {
      clearInterval(this.pollTimer);
      this.pollTimer = null;
    }
  }
}
